import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";

interface McqAssessmentQuestionCardProps {
  question: string;
  choices: string[];
  selectedChoiceIndices: number[];
  onSelectChoice: (index: number) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  disabled?: boolean;
}

const McqAssessmentQuestionCard = ({
  question,
  choices,
  selectedChoiceIndices,
  onSelectChoice,
  onSubmit,
  isSubmitting = false,
  disabled = false,
}: McqAssessmentQuestionCardProps) => {
  return (
    <View style={styles.container}>
      <CustomText text={question} useTranslationText={false} size="xl" weight="ultraBold" style={styles.questionText} />

      <View style={styles.optionsContainer}>
        {choices.map((choice, index) => {
          const isSelected = selectedChoiceIndices.includes(index);

          return (
            <Pressable
              key={`${choice}-${index}`}
              onPress={() => onSelectChoice(index)}
              style={[styles.optionRow, isSelected && styles.optionRowSelected]}
              accessibilityRole="button"
              accessibilityState={{ selected: isSelected }}
              disabled={disabled || isSubmitting}>
              <View style={[styles.optionIndexBadge, isSelected && styles.optionIndexBadgeSelected]}>
                <CustomText
                  text={(index + 1).toString()}
                  useTranslationText={false}
                  size="s"
                  weight="ultraBold"
                  style={[styles.optionIndexText, isSelected && styles.optionIndexTextSelected]}
                />
              </View>
              <CustomText
                text={choice}
                useTranslationText={false}
                size="m"
                style={[styles.optionText, isSelected && styles.optionTextSelected]}
              />
            </Pressable>
          );
        })}
      </View>

      <CustomButton
        text={isSubmitting ? "Submitting..." : "Submit Answer"}
        onPress={onSubmit}
        styleType="primary"
        style={styles.submitButton}
        textStyle={styles.submitButtonText}
        disabled={disabled || isSubmitting || selectedChoiceIndices.length === 0}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    maxWidth: 700,
    alignItems: "center",
    gap: 16,
  },
  questionText: {
    color: "#101828",
    textAlign: "center",
  },
  optionsContainer: {
    width: "100%",
    gap: 10,
  },
  optionRow: {
    width: "100%",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#EAECF0",
    backgroundColor: "#FFFFFF",
    minHeight: 48,
    paddingHorizontal: 14,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  optionRowSelected: {
    borderColor: "#3E68FE",
    backgroundColor: "#EEF4FF",
  },
  optionIndexBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    backgroundColor: "#F9FAFB",
    alignItems: "center",
    justifyContent: "center",
  },
  optionIndexBadgeSelected: {
    borderColor: "#3E68FE",
    backgroundColor: "#DDE6FF",
  },
  optionIndexText: {
    color: "#667085",
  },
  optionIndexTextSelected: {
    color: "#2643B8",
  },
  optionText: {
    color: "#344054",
    flexShrink: 1,
  },
  optionTextSelected: {
    color: "#1D2939",
  },
  submitButton: {
    width: "100%",
    maxWidth: 700,
    height: 44,
    borderRadius: 8,
    backgroundColor: "#6D8CFF",
    marginTop: 4,
  },
  submitButtonText: {
    fontFamily: "Inter",
    fontWeight: "700",
    fontSize: 14,
  },
});

export default McqAssessmentQuestionCard;
